import { isNotNil } from "@vendrinc/frontend-ts";
import algoliasearch from "algoliasearch";
import * as React from "react";
import { useDebounce } from "./useDebounce";
import { AnalyticsContext } from "~/contexts/analyticsContext";
import { SearchContext } from "~/contexts/searchContext";

export enum AlgoliaSearchResultType {
  Default = "Default",
  ProductTier = "ProductTier",
}

export type AlgoliaSearchResult =
  | AlgoliaDefaultSearchResult
  | AlgoliaProductTierSearchResult;

export type AlgoliaDefaultSearchResult = {
  type: AlgoliaSearchResultType.Default;
  hitIndex: number;
  queryID?: string | undefined;
  objectID: string;
  objectType: ObjectType;
  name: string;
  logo?: string;
  _highlightResult: { name: { value: string }; parentName: { value: string } };
  companySlug: string;
  parentId?: string;
  parentName?: string;
  category: string;
};

export type AlgoliaProductTierSearchResult = {
  type: AlgoliaSearchResultType.ProductTier;
  hitIndex: number;
  queryID?: string | undefined;
  objectID: string;
  name: string;
  objectType: ObjectType;
  logo?: string;
  _highlightResult: {
    name: { value: string };
    parentName: { value: string };
    description: { value: string };
  };
  companySlug: string;
  productId: string;
  productName: string;
  parentId?: string;
  parentName?: string;
  category: string;
  vendrCertified: boolean;
  documentCount: number;
  hasPricing: boolean;
};

export const ObjectType = {
  Product: "Product",
  Company: "Company",
  Sku: "Sku",
} as const;

export type ObjectType = (typeof ObjectType)[keyof typeof ObjectType];

const searchConfigOptions: Record<
  AlgoliaSearchResultType,
  {
    attributesToRetrieve: string[];
    restrictSearchableAttributes: string[];
    typoTolerance?: boolean;
  }
> = {
  [AlgoliaSearchResultType.Default]: {
    attributesToRetrieve: [
      "name",
      "companySlug",
      "logo",
      "parentId",
      "parentName",
      "objectType",
      "category",
    ],
    restrictSearchableAttributes: [
      "parentName",
      "name",
      "aliases",
      "domain",
      // There's also description and category, but those seem to give more false positives.
    ],
  },
  [AlgoliaSearchResultType.ProductTier]: {
    attributesToRetrieve: [
      "name",
      "companySlug",
      "logo",
      "parentId",
      "parentName",
      "objectType",
      "productId",
      "productName",
      "category",
      "vendrCertified",
      "documentCount",
      "hasPricing",
    ],
    restrictSearchableAttributes: ["parentName", "name", "aliases", "domain"],
    typoTolerance: false,
  },
};

export type ProductDefaultFacet = never;
export type ProductTierFacet = "category" | "parentCategory";
export type AlgoliaFacet = ProductDefaultFacet | ProductTierFacet;

export type Props = {
  searchResultType: AlgoliaSearchResultType;
  debounceTimeoutInMilliseconds?: number;
  hitsPerPage?: number;
  filters?: string;
};

type AlgoliaSearchResultByType<T extends AlgoliaSearchResultType> =
  T extends AlgoliaSearchResultType.Default
    ? AlgoliaDefaultSearchResult
    : AlgoliaProductTierSearchResult;

type AlgoliaFacetSearchType<T extends AlgoliaSearchResultType> =
  T extends AlgoliaSearchResultType.Default
    ? ProductDefaultFacet
    : ProductTierFacet;

export const useAlgolia = <T extends AlgoliaSearchResultType>(
  props: Props,
): {
  isRequestActive: boolean;
  search: (searchTerm: string) => Promise<AlgoliaSearchResultByType<T>[]>;
  facetSearch: (
    facet: AlgoliaFacetSearchType<T>,
    value: string,
  ) => Promise<AlgoliaSearchResultByType<T>[]>;
  indexName: string;
  hitsPerPage: number;
} => {
  const { anonymousId } = React.useContext(AnalyticsContext);

  const debounce = useDebounce({
    timeoutInMilliseconds: props?.debounceTimeoutInMilliseconds ?? 150,
  });

  const [isRequestActive, setIsRequestActive] = React.useState(false);

  const [cache, setCache] = React.useState(
    {} as {
      [key: string]: AlgoliaSearchResultByType<T>[];
    },
  );

  const searchContext = React.useContext(SearchContext);

  if (
    !searchContext.appId ||
    !searchContext.searchKey ||
    !searchContext.defaultIndex ||
    !searchContext.productTierIndex
  ) {
    return {
      isRequestActive: false,
      search: async () => [],
      facetSearch: async () => [],
      indexName: "",
      hitsPerPage: 0,
    };
  }

  const client = algoliasearch(searchContext.appId, searchContext.searchKey);
  const defaultIndex = client.initIndex(searchContext.defaultIndex);
  const productTierIndex = client.initIndex(searchContext.productTierIndex);

  const searchIndex =
    props.searchResultType === AlgoliaSearchResultType.ProductTier
      ? productTierIndex
      : defaultIndex;

  const hitsPerPage = props?.hitsPerPage ?? 5;

  const search = async (
    searchTerm: string,
  ): Promise<AlgoliaSearchResultByType<T>[]> => {
    const trimmedSearchTerm = searchTerm.trim();

    if (
      cache[trimmedSearchTerm] !== undefined &&
      cache[trimmedSearchTerm] !== null
    ) {
      return cache[trimmedSearchTerm];
    }

    setIsRequestActive(true);

    try {
      const { hits, queryID } = await new Promise<{
        hits: AlgoliaSearchResultByType<T>[];
        queryID?: string;
      }>((resolve) => {
        debounce(async () => {
          const searchConfig = searchConfigOptions[props.searchResultType];

          const resp = await searchIndex.search<AlgoliaSearchResultByType<T>>(
            trimmedSearchTerm,
            {
              ...searchConfig,
              hitsPerPage,
              clickAnalytics: true,
              userToken: anonymousId ?? undefined,
              filters: props?.filters,
            },
          );

          resolve(resp);
        });
      });

      // Add queryID and index for algolia insights
      const suggestions: AlgoliaSearchResultByType<T>[] = hits
        .map((h, i) => ({
          ...h,
          hitIndex: i + 1, // 1-based indexing for algolia
          queryID: queryID,
        }))
        .filter((h) => isNotNil(h.companySlug));

      setCache((currentCache) => ({
        ...currentCache,
        [trimmedSearchTerm]: suggestions,
      }));

      return suggestions;
    } finally {
      setIsRequestActive(false);
    }
  };

  const facetSearch = async (
    facet: AlgoliaFacetSearchType<T>,
    value: string,
  ): Promise<AlgoliaSearchResultByType<T>[]> => {
    // Don't pass in a search term, just facet filters
    // and the result should then be ranked/sorted by document count
    const resp = await searchIndex.search<AlgoliaSearchResultByType<T>>("", {
      facetFilters: [`${facet}:${value}`],
      hitsPerPage: props.hitsPerPage ?? 20,
    });

    const results: AlgoliaSearchResultByType<T>[] = resp.hits
      .map((h, i) => ({
        ...h,
        hitIndex: i + 1,
        queryID: resp.queryID,
      }))
      .filter((h) => isNotNil(h.companySlug));

    return results;
  };

  return {
    isRequestActive,
    search,
    facetSearch,
    indexName: searchIndex.indexName,
    hitsPerPage,
  };
};
